import React, { createContext, useContext } from "react";
import {
  AutoForm as BaseAutoForm,
  AutoFormUIComponents,
} from "@autoform/react";
import { AutoFormProps } from "./types";
import { Form } from "./components/Form";
import { FieldWrapper } from "./components/FieldWrapper";
import { ErrorMessage } from "./components/ErrorMessage";
import { SubmitButton } from "./components/SubmitButton";
import { StringField } from "./components/StringField";
import { NumberField } from "./components/NumberField";
import { BooleanField } from "./components/BooleanField";
import { DateField } from "./components/DateField";
import { SelectField } from "./components/SelectField";
import { AddressField } from "./components/AddressField";
import { PhoneField } from "./components/PhoneField";
import { ObjectWrapper } from "./components/ObjectWrapper";
import { ArrayWrapper } from "./components/ArrayWrapper";
import { ArrayElementWrapper } from "./components/ArrayElementWrapper";
import { TimeField } from "./components/TimeField";
import { URLField } from "./components/URLField";

const ShadcnUIComponents: AutoFormUIComponents = {
  Form,
  FieldWrapper,
  ErrorMessage,
  SubmitButton,
  ObjectWrapper,
  ArrayWrapper,
  ArrayElementWrapper,
};

export const ShadcnAutoFormFieldComponents = {
  string: StringField,
  number: NumberField,
  boolean: BooleanField,
  date: DateField,
  time: TimeField,
  select: SelectField,
  address: AddressField,
  phone: PhoneField,
  url: URLField,
} as const;
export type FieldTypes = keyof typeof ShadcnAutoFormFieldComponents;

export function AutoForm<T extends Record<string, any>>({
  uiComponents,
  formComponents,
  ...props
}: AutoFormProps<T>) {
  const defaults = props.defaultValues ?? props.values;
  return (
    <AutoFormDefaultValueProvider defaultValues={defaults}>
      <BaseAutoForm
        {...props}
        values={defaults}
        defaultValues={defaults}
        uiComponents={{ ...ShadcnUIComponents, ...uiComponents }}
        formComponents={{ ...ShadcnAutoFormFieldComponents, ...formComponents }}
      />
    </AutoFormDefaultValueProvider>
  );
}

const AutoFormDefaultValueContext = createContext<unknown>({});

export function AutoFormDefaultValueProvider<T>({
  children,
  defaultValues,
}: {
  children: React.ReactNode;
  defaultValues: unknown;
}) {
  return (
    <AutoFormDefaultValueContext.Provider value={defaultValues}>
      {children}
    </AutoFormDefaultValueContext.Provider>
  );
}

export const useAutoFormDefaultValues = () => {
  return useContext(AutoFormDefaultValueContext);
};
